import { ChallengeDifficultyBadge, ChallengeTypeBadge } from '@/components/challenges';
import { VisibilityBadge } from '@/components/shared';
import type { Challenge } from '@/config/objects/challenges';
import {
  Button,
  ConfirmDialog,
  EntityDetailShell,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Gamepad2, Lightbulb, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const tabs = [
  { label: 'Overview', path: 'overview', icon: Lightbulb },
  { label: 'Demo', path: 'demo', icon: Gamepad2 },
] as const;

export function ChallengeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Challenge | null>(null);
  const basePath = `/challenges/${id}`;

  const deleteMutation = useMutation({
    mutationFn: async (
      challengeId: string,
    ): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/challenges/${challengeId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['Challenge'] });
        navigate('/challenges');
      } else {
        throw new Error(response.error || 'Failed to delete challenge');
      }
    },
  });

  const handleDelete = () => {
    if (!pendingDelete) return;
    toast.promise(deleteMutation.mutateAsync(pendingDelete.id), {
      loading: 'Deleting challenge...',
      success: 'Challenge deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete challenge',
    });
  };

  return (
    <>
      <EntityDetailShell<Challenge>
        resource="challenges"
        queryKey="Challenge"
        id={id}
        basePath={basePath}
        entityLabel="Challenge"
        tabs={tabs}
        contextKey="challenge"
        hero={{
          category: { label: 'Challenge', icon: Lightbulb },
          title: (c) => c?.name || 'Challenge',
          imageUrl: (c) => c?.imageUrl ?? null,
          videoUrl: (c) => c?.videoUrl ?? null,
          fallbackIcon: Lightbulb,
          badges: (c) => (
            <>
              {c.type && <ChallengeTypeBadge type={c.type} variant="solid" />}
              {c.difficulty && (
                <ChallengeDifficultyBadge difficulty={c.difficulty} variant="solid" />
              )}
              {c.visibility && <VisibilityBadge visibility={c.visibility} variant="solid" />}
              {c.points !== undefined && (
                <span className="text-sm text-white/70">
                  {c.points} {c.points === 1 ? 'point' : 'points'}
                </span>
              )}
            </>
          ),
        }}
        breadcrumbs={(c) => [
          { label: 'Home', href: '/' },
          { label: 'Challenge Library', href: '/challenges' },
          { label: c?.name || 'Challenge', current: true },
        ]}
        headerActions={(c) =>
          c ? (
            <>
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setPendingDelete(c);
                  setShowDeleteConfirm(true);
                }}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          ) : null
        }
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Challenge"
        description={`Are you sure you want to delete "${pendingDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
}
